import React, { useEffect, useMemo, useState } from "react";
import { authFetch, isAdmin } from "../../auth";
import { ActionModal } from "../Common/ActionModal";

function toLocalDateTimeText(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("zh-CN", { hour12: false });
}

function toNullableInt(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

function toNonNegativeNullableInt(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return null;
  return n;
}

function formatCoordinate(row) {
  if (row?.longitude == null || row?.latitude == null) return "-";
  return `${Number(row.longitude).toFixed(6)}, ${Number(row.latitude).toFixed(6)}`;
}

function formatGpsState(value) {
  if (value == null) return "-";
  switch (Number(value)) {
    case 2:
      return "有效";
    case 3:
      return "无效";
    case 4:
      return "未接GPS";
    default:
      return `未知状态(${value})`;
  }
}

function readonlyItem(label, value) {
  return (
    <div className="col-md-4" key={label}>
      <div className="text-muted small">{label}</div>
      <div>{value ?? "-"}</div>
    </div>
  );
}

export function DeviceManagement() {
  const admin = isAdmin();

  const [deviceNo, setDeviceNo] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [netState, setNetState] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerError, setCustomerError] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupError, setGroupError] = useState("");
  const [editCustomerId, setEditCustomerId] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [createModal, setCreateModal] = useState({ open: false });
  const [createCustomerId, setCreateCustomerId] = useState("");
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    if (!admin) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    if (!admin) return;
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, page]);

  useEffect(() => {
    if (!admin || (!createModal.open && !editModal.open)) return;
    loadGroupOptions(createModal.open ? createCustomerId : editCustomerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, createModal.open, createCustomerId, editModal.open, editCustomerId]);

  async function loadCustomerOptions() {
    try {
      const res = await authFetch("/api/customers/options");
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "客户列表加载失败");
      }

      const data = await res.json();
      setCustomers(Array.isArray(data?.items) ? data.items : []);
      setCustomerError("");
    } catch (err) {
      setCustomers([]);
      setCustomerError(err?.message || "客户列表加载失败");
    }
  }

  async function loadGroupOptions(nextCustomerId) {
    if (!nextCustomerId) {
      setGroups([]);
      setGroupError("");
      return;
    }

    setGroups([]);
    try {
      const qs = new URLSearchParams({
        page: "1",
        pageSize: "200",
        customerId: String(nextCustomerId)
      });
      const res = await authFetch(`/api/groups?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "分组列表加载失败");
      }

      const data = await res.json();
      setGroups(Array.isArray(data?.items) ? data.items : []);
      setGroupError("");
    } catch (err) {
      setGroups([]);
      setGroupError(err?.message || "分组列表加载失败");
    }
  }

  async function loadDevices(resetMsg = false) {
    setLoading(true);
    setError("");
    if (resetMsg) setMessage("");

    try {
      const qs = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });

      if (deviceNo.trim()) qs.set("deviceNo", deviceNo.trim());
      if (vehicleNo.trim()) qs.set("vehicleNo", vehicleNo.trim());
      if (netState !== "") qs.set("netState", netState);
      if (customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/devices?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "设备查询失败");
      }

      const data = await res.json();
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "设备查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setTimeout(() => loadDevices(true), 0);
  }

  function openCreateModal() {
    setCreateCustomerId(customerId.trim());
    setGroupError("");
    setCreateModal({ open: true });
  }

  async function submitCreateDevice(form) {
    const nextDeviceNo = (form.deviceNo || "").trim();
    const nextVehicleNo = (form.vehicleNo || "").trim();
    const nextCustomerId = toNullableInt(form.customerId);
    const nextGroupId = toNullableInt(form.groupId);
    const nextOverspeedThreshold = toNonNegativeNullableInt(form.overspeedThreshold);

    if (!nextCustomerId) {
      setError("客户不能为空。");
      return;
    }
    if (!nextDeviceNo) {
      setError("设备号不能为空。");
      return;
    }
    if (!nextVehicleNo) {
      setError("车牌号不能为空。");
      return;
    }
    if ((form.groupId || "").trim() !== "" && nextGroupId == null) {
      setError("分组必须选择有效值或留空。");
      return;
    }
    if ((form.overspeedThreshold || "").trim() !== "" && nextOverspeedThreshold == null) {
      setError("超速阈值必须是非负整数或留空。");
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch("/api/devices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: nextCustomerId,
          groupId: nextGroupId,
          deviceNo: nextDeviceNo,
          vehicleNo: nextVehicleNo,
          hardwareVersion: (form.hardwareVersion || "").trim() || null,
          softwareVersion: (form.softwareVersion || "").trim() || null,
          overspeedThreshold: nextOverspeedThreshold
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "新增设备失败");
        return;
      }

      setCreateModal({ open: false });
      setCreateCustomerId("");
      setMessage(`设备 ${nextDeviceNo} 新增成功。`);
      if (page === 1) {
        await loadDevices();
      } else {
        setPage(1);
      }
    } finally {
      setModalSubmitting(false);
    }
  }

  function openEditModal(row) {
    setEditCustomerId(row?.customerId == null ? "" : String(row.customerId));
    setGroupError("");
    setEditModal({ open: true, row });
  }

  async function submitUpdateDevice(form) {
    const row = editModal.row;
    if (!row) return;

    const nextDeviceNo = (form.deviceNo || "").trim();
    const nextVehicleNo = (form.vehicleNo || "").trim();
    const nextCustomerId = toNullableInt(form.customerId);
    const nextGroupId = toNullableInt(form.groupId);
    const nextOverspeedThreshold = toNonNegativeNullableInt(form.overspeedThreshold);

    if (!nextDeviceNo) {
      setError("设备号不能为空。");
      return;
    }
    if (!nextVehicleNo) {
      setError("车牌号不能为空。");
      return;
    }
    if ((form.customerId || "").trim() !== "" && nextCustomerId == null) {
      setError("客户必须选择有效值。");
      return;
    }
    if ((form.groupId || "").trim() !== "" && nextGroupId == null) {
      setError("分组必须选择有效值或留空。");
      return;
    }
    if ((form.overspeedThreshold || "").trim() !== "" && nextOverspeedThreshold == null) {
      setError("超速阈值必须是非负整数或留空。");
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/devices/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId: nextCustomerId,
          groupId: nextGroupId,
          deviceNo: nextDeviceNo,
          vehicleNo: nextVehicleNo,
          hardwareVersion: (form.hardwareVersion || "").trim() || null,
          softwareVersion: (form.softwareVersion || "").trim() || null,
          overspeedThreshold: nextOverspeedThreshold
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "更新设备失败");
        return;
      }

      setEditModal({ open: false, row: null });
      setMessage(`设备 ${nextDeviceNo} 更新成功。`);
      await loadDevices();
    } finally {
      setModalSubmitting(false);
    }
  }

  function openDeleteModal(row) {
    setDeleteModal({ open: true, row });
  }

  async function submitDeleteDevice() {
    const row = deleteModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/devices/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "删除设备失败");
        return;
      }

      setDeleteModal({ open: false, row: null });
      setMessage(`设备 ${row.deviceNo} 删除成功。`);
      await loadDevices();
    } finally {
      setModalSubmitting(false);
    }
  }

  const customerOptions = [
    { value: "", label: "未分配客户" },
    ...customers.map((customer) => ({
      value: String(customer.id),
      label: `${customer.name}${customer.code ? ` / ${customer.code}` : ""} #${customer.id}`
    }))
  ];

  const editRow = editModal.row;
  const activeCustomerId = createModal.open ? createCustomerId : editCustomerId;
  const groupOptions = useMemo(() => {
    const options = [{ value: "", label: activeCustomerId ? "未分组" : "未分组（请先选择客户）" }];
    if (!activeCustomerId) {
      return options;
    }

    const mapped = groups.map((group) => ({
      value: String(group.id),
      label: `${group.name} #${group.id}`
    }));
    const hasCurrent = editRow?.groupId != null && mapped.some((group) => group.value === String(editRow.groupId));
    if (!createModal.open && !hasCurrent && editRow?.groupId != null && String(editRow.customerId ?? "") === activeCustomerId) {
      mapped.unshift({
        value: String(editRow.groupId),
        label: `${editRow.groupName || "当前分组"} #${editRow.groupId}`
      });
    }

    return [...options, ...mapped];
  }, [activeCustomerId, createModal.open, editRow, groups]);

  const createInitialValues = useMemo(() => ({
    customerId: createCustomerId,
    groupId: "",
    deviceNo: "",
    vehicleNo: "",
    hardwareVersion: "",
    softwareVersion: "",
    overspeedThreshold: ""
  }), [createCustomerId]);

  const editInitialValues = useMemo(() => ({
    customerId: editRow?.customerId == null ? "" : String(editRow.customerId),
    groupId: editRow?.groupId == null ? "" : String(editRow.groupId),
    deviceNo: editRow?.deviceNo || "",
    vehicleNo: editRow?.vehicleNo || "",
    hardwareVersion: editRow?.hardwareVersion || "",
    softwareVersion: editRow?.softwareVersion || "",
    overspeedThreshold: editRow?.overspeedThreshold == null ? "" : String(editRow.overspeedThreshold)
  }), [editRow]);

  if (!admin) {
    return (
      <div className="container-fluid">
        <div className="status-banner warn mt-3">当前账号无设备管理权限。</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <span>设备管理</span>
          <button type="button" className="btn btn-light btn-sm" onClick={openCreateModal}>
            新增设备
          </button>
        </div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-3">
              <label className="form-label">设备号</label>
              <input className="form-control" value={deviceNo} onChange={(e) => setDeviceNo(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">车牌号</label>
              <input className="form-control" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label">在线状态</label>
              <select className="form-select" value={netState} onChange={(e) => setNetState(e.target.value)}>
                <option value="">全部</option>
                <option value="1">在线</option>
                <option value="0">离线</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">客户</label>
              <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">全部客户</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={String(customer.id)}>
                    {customer.name}{customer.code ? ` / ${customer.code}` : ""} #{customer.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {customerError && <div className="status-banner warn mt-3">客户列表加载失败：{customerError}</div>}
          {groupError && <div className="status-banner warn mt-3">分组列表加载失败：{groupError}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>设备号</th>
                  <th>车牌号</th>
                  <th>客户</th>
                  <th>分组</th>
                  <th>在线</th>
                  <th>最近位置</th>
                  <th>最近GPS时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <div className="status-banner empty m-0">暂无设备数据</div>
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.deviceNo || "-"}</td>
                    <td>{row.vehicleNo || "-"}</td>
                    <td>
                      {row.customerName || "-"}
                      {row.customerId != null && <small className="text-muted d-block">#{row.customerId}</small>}
                    </td>
                    <td>{row.groupName || (row.groupId ? `#${row.groupId}` : "-")}</td>
                    <td>
                      <span className={`badge ${row.netState === 1 ? "bg-success" : "bg-secondary"}`}>
                        {row.netState === 1 ? "在线" : "离线"}
                      </span>
                    </td>
                    <td title={row.positionDesc || ""}>{row.positionDesc || "-"}</td>
                    <td>{toLocalDateTimeText(row.lastGpsTime)}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openEditModal(row)}>
                          编辑
                        </button>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => openDeleteModal(row)}>
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page >= pageCount || loading}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>

      <ActionModal
        open={createModal.open}
        title="新增设备"
        message="新增设备初始状态为离线，首次上报后会更新定位和状态。"
        fields={[
          { name: "customerId", label: "客户", type: "select", options: customerOptions, required: true, clearOnChange: ["groupId"] },
          { name: "groupId", label: "分组", type: "select", options: groupOptions, hint: "留空表示未分组" },
          { name: "deviceNo", label: "设备号", required: true },
          { name: "vehicleNo", label: "车牌号", required: true },
          { name: "hardwareVersion", label: "硬件版本" },
          { name: "softwareVersion", label: "软件版本" },
          { name: "overspeedThreshold", label: "超速阈值", type: "number", hint: "非负整数，留空表示未设置" }
        ]}
        initialValues={createInitialValues}
        confirmText="新增"
        submitting={modalSubmitting}
        onChange={(next) => setCreateCustomerId(next.customerId || "")}
        onCancel={() => {
          setCreateCustomerId("");
          setCreateModal({ open: false });
        }}
        onConfirm={submitCreateDevice}
      />

      <ActionModal
        open={editModal.open}
        title="编辑设备"
        message={editRow ? `设备：${editRow.deviceNo}` : ""}
        fields={[
          { name: "customerId", label: "客户", type: "select", options: customerOptions, clearOnChange: ["groupId"] },
          { name: "groupId", label: "分组", type: "select", options: groupOptions, hint: "留空表示清空分组" },
          { name: "deviceNo", label: "设备号", required: true },
          { name: "vehicleNo", label: "车牌号", required: true },
          { name: "hardwareVersion", label: "硬件版本" },
          { name: "softwareVersion", label: "软件版本" },
          { name: "overspeedThreshold", label: "超速阈值", type: "number", hint: "非负整数，留空表示未设置" }
        ]}
        initialValues={editInitialValues}
        confirmText="保存"
        submitting={modalSubmitting}
        onChange={(next) => setEditCustomerId(next.customerId || "")}
        onCancel={() => {
          setEditCustomerId("");
          setEditModal({ open: false, row: null });
        }}
        onConfirm={submitUpdateDevice}
      >
        {editRow && (
          <div className="row g-2 mt-2 border-top pt-2">
            {readonlyItem("ID", editRow.id)}
            {readonlyItem("客户名称", editRow.customerName)}
            {readonlyItem("分组名称", editRow.groupName)}
            {readonlyItem("在线状态", editRow.netState === 1 ? "在线" : "离线")}
            {readonlyItem("GPS 状态", formatGpsState(editRow.gpsState))}
            {readonlyItem("SIM 数量", editRow.simCount ?? "-")}
            {readonlyItem("SIM1", editRow.simId1)}
            {readonlyItem("SIM2", editRow.simId2)}
            {readonlyItem("设备上报时间", toLocalDateTimeText(editRow.reportDeviceTime))}
            {readonlyItem("设备上报 IP", editRow.reportIpAddress)}
            {readonlyItem("上电标记", editRow.powerOnFlag ?? "-")}
            {readonlyItem("MCU 重启计数", editRow.mcuResetCount ?? "-")}
            {readonlyItem("GPRS 重启计数1", editRow.gprsModuleResetCount1 ?? "-")}
            {readonlyItem("GPRS 重启计数2", editRow.gprsModuleResetCount2 ?? "-")}
            {readonlyItem("最近状态上报", toLocalDateTimeText(editRow.lastReportStatusTime))}
            {readonlyItem("当前速度", editRow.speed ?? "-")}
            {readonlyItem("方向", editRow.direction ?? "-")}
            {readonlyItem("经纬度", formatCoordinate(editRow))}
            {readonlyItem("位置描述", editRow.positionDesc)}
            {readonlyItem("最近 GPS 时间", toLocalDateTimeText(editRow.lastGpsTime))}
            {readonlyItem("最近更新时间", toLocalDateTimeText(editRow.lastUpdateTime))}
          </div>
        )}
      </ActionModal>

      <ActionModal
        open={deleteModal.open}
        title="删除设备"
        message={deleteModal.row ? `确认删除设备 ${deleteModal.row.deviceNo}？存在轨迹、报警、指令或升级记录的设备不会被删除。` : ""}
        fields={[]}
        confirmText="删除"
        confirmClassName="btn-danger"
        submitting={modalSubmitting}
        onCancel={() => setDeleteModal({ open: false, row: null })}
        onConfirm={submitDeleteDevice}
      />
    </div>
  );
}
